import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';
import TimingConfiguration from './TimingConfiguration';
import AutomationFeatures from './AutomationFeatures';
import PrimaryButton from '../../../components/common/PrimaryButton';
import SecondaryButton from '../../../components/common/SecondaryButton';
import AutomationService from "../.../../../../services/AutomationService";
import { CommunicationTypeEnum } from "../../../enums/CommunicationTypeEnum";
import { FollowUpChannelEnum } from "../../../enums/FollowUpChannelEnum";
import { FollowUpTimingEnum } from "../../../enums/FollowUpTimingEnum";


const AutomationDesigner = ({ onUnsavedChanges }) => {
  const [showSettings, setShowSettings] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followUpSettingsResponse, setFollowUpSettingsResponse] = useState([]); 
  const [saving, setSaving] = useState(false);
 

  // Notify parent component when changes occur - debounced
  useEffect(() => {
  const fetchWebinarFollowUpSettings = async () => {
    try {
      const response = await AutomationService.fetchWebinarFollowUpSettings();

      if (response.data?.success) {
        setFollowUpSettingsResponse(response.data.data);
      } else {
        setError("Failed to fetch settings.");
      }
    } catch (err) {
      console.error("API error:", err);
      setError("An error occurred while fetching settings.");
    } finally {
      setLoading(false);
    }
  };

  fetchWebinarFollowUpSettings();
}, []);

  // THIS IS WHERE CHANGES ARE SAVED
  const handleTimingChange = (recipientId, channelId, updatedApiTimings) => {
    console.log("Update Time ", updatedApiTimings)
    setFollowUpSettingsResponse(prev =>
      prev.map(setting =>
        setting.recipientId === recipientId &&
        setting.channelId === channelId &&
        setting.communicationTypeId === CommunicationTypeEnum.BeforeEvent
          ? { ...setting, followUpTimings: updatedApiTimings }
          : setting
      )
    );
  };

  const registrationEmailList = followUpSettingsResponse
  .filter(
    item =>
      item.communicationTypeId === CommunicationTypeEnum.Registration &&
      item.channelId === FollowUpChannelEnum.Email
  )
  .sort((a, b) => a.recipientId - b.recipientId);

const registrationWhatsappList = followUpSettingsResponse
  .filter(
    item =>
      item.communicationTypeId === CommunicationTypeEnum.Registration &&
      item.channelId === FollowUpChannelEnum.WhatsApp
  )
  .sort((a, b) => a.recipientId - b.recipientId);

const beforeEventfollowUpEmailList = followUpSettingsResponse
  .filter(
    item =>
      item.communicationTypeId === CommunicationTypeEnum.BeforeEvent &&
      item.channelId === FollowUpChannelEnum.Email
  )
  .sort((a, b) => a.recipientId - b.recipientId);

const beforeEventfollowUpWhatsappList = followUpSettingsResponse
  .filter(
    item =>
      item.communicationTypeId === CommunicationTypeEnum.BeforeEvent &&
      item.channelId === FollowUpChannelEnum.WhatsApp
  )
  .sort((a, b) => a.recipientId - b.recipientId);

const afterEventfollowUpEmailList = followUpSettingsResponse
  .filter(
    item =>
      item.communicationTypeId === CommunicationTypeEnum.AfterEvent &&
      item.channelId === FollowUpChannelEnum.Email
  )
  .sort((a, b) => a.recipientId - b.recipientId);

  const afterEventfollowUpWhatsappList = followUpSettingsResponse
  .filter(
    item =>
      item.communicationTypeId === CommunicationTypeEnum.AfterEvent &&
      item.channelId === FollowUpChannelEnum.WhatsApp
  )
  .sort((a, b) => a.recipientId - b.recipientId);

 const handleToggle = (communicationTypeId, channelId, recipientId, value) => {
  setFollowUpSettingsResponse(prev => {
    if (!Array.isArray(prev)) return prev;

    return prev.map(item =>
      item.communicationTypeId === communicationTypeId &&
      item.channelId === channelId &&
      item.recipientId === recipientId
        ? { ...item, isEnabled: value }
        : item
    );
  });
};


  const buildSavePayload = () => ({
    followUpSettingRequests: followUpSettingsResponse.map(setting => ({
      id: setting.id ?? null,
      businessAdminId: setting.businessAdminId,
      channelId: Number(setting.channelId),
      recipientId: Number(setting.recipientId),
      communicationTypeId: Number(setting.communicationTypeId),
      isEnabled: Boolean(setting.isEnabled),

      followUpTimings: (setting.followUpTimings || []).map(timing => ({
        id: timing.id ?? null, // null = new timing
        followUpSettingId: setting.id,
        timeValue: Number(timing.timeValue),
        timeUnit: Number(timing.timeUnit),
        offsetDescription:
          timing.offsetDescription ??
          `${timing.timeValue} ${getUnitText(timing.timeUnit)} before webinar`
      }))
    }))
  });


  const getUnitText = (unit) => {
    switch (unit) {
      case FollowUpTimingEnum.Minutes:
        return "minutes";
      case FollowUpTimingEnum.Hours:
        return "hours";
      case FollowUpTimingEnum.Days:
        return "days";
      default:
        return "";
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = buildSavePayload();

      const response =
        await AutomationService.updateFollowUpSettings(payload);

      if (response.data?.success) {
        alert("Automation settings saved successfully");
      } else {
        alert("Failed to save automation settings");
      }
    } catch (error) {
      console.error("Save failed:", error);
      alert("Something went wrong while saving");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Action Buttons - Right aligned */}
      <div className="d-flex justify-content-end gap-3 mb-4">
        <SecondaryButton onClick={() => setShowSettings(!showSettings)}>
          {showSettings ? 'Hide Settings' : 'Customize Automation'}
        </SecondaryButton>
        <PrimaryButton onClick={handleSave}>
          Save
        </PrimaryButton>
      </div>

      {/* Lead Generation Automation Designer */}
      <div className="text-center mb-5">
        <h3 className="fw-semibold mb-2" style={{ fontSize: '1.25rem' }}>Webinar Automation</h3>
        <p className="text-muted-foreground">Design your automated workflow for webinar lead generation and follow-up</p>
      </div>

      {/* Workflow Visualization */}
      <div className="d-flex align-items-center justify-content-center gap-4 mb-5">
        <Card className="automation-card text-center" style={{ width: '200px', padding: '20px' }}>
          <div className="automation-icon-wrapper mb-3">
            <i className="bi bi-person-plus fs-1 text-primary"></i>
          </div>
          <h6 className="fw-bold mb-1">Lead Entry</h6>
          <small className="text-muted">New lead registers</small>
        </Card>
        
        <div style={{ fontSize: '24px', color: '#6c757d' }}>
          <i className="bi bi-arrow-right"></i>
        </div>
        
        <Card className="automation-card text-center" style={{ width: '200px', padding: '20px' }}>
          <div className="automation-icon-wrapper mb-3">
            <i className="bi bi-calendar-event fs-1 text-success"></i>
          </div>
          <h6 className="fw-bold mb-1">Webinar Registration</h6>
          <small className="text-muted">Automatic enrollment</small>
        </Card>
      </div>

      {/* Configuration Settings - Only show when showSettings is true */}
      {showSettings && (
        <>
          {/* Immediate Registration Communications */}
          <div className="mb-5">
            <div className="d-flex align-items-center justify-content-center mb-3">
              <i className="bi bi-check-circle-fill text-success me-2"></i>
              <h5 className="fw-bold mb-0">Immediate Registration Communications</h5>
            </div>
            <p className="text-center text-muted mb-4">Sent immediately after registration is completed</p>
            
            <div className="d-flex justify-content-center align-items-center gap-4 mb-4">
              <Card className="automation-card text-center" style={{ width: '280px' }}>
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <div className="automation-icon-wrapper mb-2">
                      <i className="bi bi-envelope fs-2"></i>
                    </div>
                  </div>
                  <h6 className="fw-bold mb-2">Registration Email</h6>
                  <p className="text-muted small mb-3">Sent to admin & leads</p>
                  <div className="text-start">
                    { registrationEmailList.map(item => (
                        <Form.Check
                          key={item.id}
                          type="checkbox"
                          label={item.recipient.name}
                          checked={item.isEnabled}
                          className="mb-2"
                          onChange={(e) =>
                            handleToggle(
                              item.communicationTypeId,
                              item.channelId,
                              item.recipientId,
                              e.target.checked
                            )
                          }
                        />
                      ))}
                  </div>
                </Card.Body>
              </Card>
              
              <div className="and-connector">
                <span className="and-badge">AND</span>
              </div>
              
              <Card className="automation-card text-center" style={{ width: '280px' }}>
                <Card.Body className="p-4">
                  <div className="mb-3">
                    <div className="automation-icon-wrapper mb-2">
                      <i className="bi bi-chat-dots fs-2"></i>
                    </div>
                  </div>
                  <h6 className="fw-bold mb-2">Registration WhatsApp</h6>
                  <p className="text-muted small mb-3">Sent to admin & leads</p>
                  <div className="text-start">
                    { registrationWhatsappList.map(item => (
                        <Form.Check
                          key={item.id}
                          type="checkbox"
                          label={item.recipient.name}
                          checked={item.isEnabled}
                          className="mb-2"
                          onChange={(e) =>
                            handleToggle(
                              item.communicationTypeId,
                              item.channelId,
                              item.recipientId,
                              e.target.checked
                            )
                          }
                        />
                      ))}
                  </div>
                </Card.Body>
              </Card>
            </div>
          </div>

          {/* Follow-up / Reminder Automations Header */}
          <div className="text-center mb-4">
            <div className="followup-header">
              <i className="bi bi-calendar-check me-2"></i>
              <span>Follow-up / Reminder Automations</span>
            </div>
          </div>

          {/* Scheduled Follow-up Communications */}
          <div className="mb-5">
            <h5 className="fw-bold text-center mb-2">Scheduled Follow-up Communications</h5>
            <p className="text-center text-muted mb-4">
              Configure automated reminders and follow-up messages that will be sent at specific times before and during your webinar. These are separate from the immediate registration confirmations above.
            </p>
            
            <div className="d-flex justify-content-center gap-4">
              <Card className="automation-card" style={{ width: '400px' }}>
                <Card.Body className="p-4">
                  <div className="text-center mb-3">
                    <div className="automation-icon-wrapper mb-2">
                      <i className="bi bi-envelope fs-2"></i>
                    </div>
                    <h6 className="fw-bold mb-2">Email Follow-ups</h6>
                    <p className="text-muted small">Scheduled email reminders</p>
                  </div>
                  
                  <div className="mb-3">
                    <p className="fw-bold mb-2 small">Send to:</p>
                    { beforeEventfollowUpEmailList.map(item => (
                        <Form.Check
                          key={item.id}
                          type="checkbox"
                          label={item.recipient.name}
                          checked={item.isEnabled}
                          className="mb-2"
                          onChange={(e) =>
                            handleToggle(
                              item.communicationTypeId,
                              item.channelId,
                              item.recipientId,
                              e.target.checked
                            )
                          }
                        />
                      ))}
                  </div>
                  
                  <TimingConfiguration 
                    title="Timing Configuration"
                    channelId={FollowUpChannelEnum.Email}
                    followUpSettings={beforeEventfollowUpEmailList}
                    onTimingChange={handleTimingChange}
                  />
                </Card.Body>
              </Card>
              
              <Card className="automation-card" style={{ width: '400px' }}>
                <Card.Body className="p-4">
                  <div className="text-center mb-3">
                    <div className="automation-icon-wrapper mb-2">
                      <i className="bi bi-chat-dots fs-2"></i>
                    </div>
                    <h6 className="fw-bold mb-2">WhatsApp Follow-ups</h6>
                    <p className="text-muted small">Scheduled WhatsApp reminders</p>
                  </div>
                  
                  <div className="mb-3">
                    <p className="fw-bold mb-2 small">Send to:</p>
                    { beforeEventfollowUpWhatsappList.map(item => (
                        <Form.Check
                          key={item.id}
                          type="checkbox"
                          label={item.recipient.name}
                          checked={item.isEnabled}
                          className="mb-2"
                          onChange={(e) =>
                            handleToggle(
                            item.communicationTypeId,
                            item.channelId,
                            item.recipientId,
                            e.target.checked
                          )
                          }
                        />
                      ))}
                  </div>
                  
                  <TimingConfiguration 
                    title="Timing Configuration" 
                    channelId={FollowUpChannelEnum.WhatsApp}
                    followUpSettings={beforeEventfollowUpWhatsappList}
                    onTimingChange={handleTimingChange}
                  />
                </Card.Body>
              </Card>
            </div>
          </div>

           {/* Follow-up / Reminder Automations Header */}
          <div className="text-center mb-4">
            <div className="followup-header">
              <i className="bi bi-calendar-check me-2"></i>
              <span>After Event Follow-up Automations</span>
            </div>
          </div>

          {/* Scheduled Follow-up Communications */}
          <div className="mb-5">
            <h5 className="fw-bold text-center mb-2">Scheduled Follow-up Communications</h5>
            <p className="text-center text-muted mb-4">
              Configure automated reminders and follow-up messages that will be sent at specific times before and during your webinar. These are separate from the immediate registration confirmations above.
            </p>
            
            <div className="d-flex justify-content-center gap-4">
              <Card className="automation-card" style={{ width: '400px' }}>
                <Card.Body className="p-4">
                  <div className="text-center mb-3">
                    <div className="automation-icon-wrapper mb-2">
                      <i className="bi bi-envelope fs-2"></i>
                    </div>
                    <h6 className="fw-bold mb-2">Email Follow-ups</h6>
                    <p className="text-muted small">Scheduled email reminders</p>
                  </div>
                  
                  <div className="mb-3">
                    <p className="fw-bold mb-2 small">Send to:</p>
                    { afterEventfollowUpEmailList.map(item => (
                        <Form.Check
                          key={item.id}
                          type="checkbox"
                          label={item.recipient.name}
                          checked={item.isEnabled}
                          className="mb-2"
                          onChange={(e) =>
                            handleToggle(
                              item.communicationTypeId,
                              item.channelId,
                              item.recipientId,
                              e.target.checked
                            )
                          }
                        />
                      ))}
                  </div>
                  
                  <TimingConfiguration 
                    title="Timing Configuration"
                    channelId={FollowUpChannelEnum.Email}
                    followUpSettings={afterEventfollowUpEmailList}
                    onTimingChange={handleTimingChange}
                  />
                </Card.Body>
              </Card>
              
              <Card className="automation-card" style={{ width: '400px' }}>
                <Card.Body className="p-4">
                  <div className="text-center mb-3">
                    <div className="automation-icon-wrapper mb-2">
                      <i className="bi bi-chat-dots fs-2"></i>
                    </div>
                    <h6 className="fw-bold mb-2">WhatsApp Follow-ups</h6>
                    <p className="text-muted small">Scheduled WhatsApp reminders</p>
                  </div>
                  
                  <div className="mb-3">
                    <p className="fw-bold mb-2 small">Send to:</p>
                    { afterEventfollowUpWhatsappList.map(item => (
                        <Form.Check
                          key={item.id}
                          type="checkbox"
                          label={item.recipient.name}
                          checked={item.isEnabled}
                          className="mb-2"
                          onChange={(e) =>
                            handleToggle(
                            item.communicationTypeId,
                            item.channelId,
                            item.recipientId,
                            e.target.checked
                          )
                          }
                        />
                      ))}
                  </div>
                  
                  <TimingConfiguration 
                    title="Timing Configuration" 
                    channelId={FollowUpChannelEnum.WhatsApp}
                    followUpSettings={afterEventfollowUpWhatsappList}
                    onTimingChange={handleTimingChange}
                  />
                </Card.Body>
              </Card>
            </div>
          </div>

          {/* Automation Features */}
          {/* <AutomationFeatures /> */}
        </>
      )}
    </div>
  );
};

export default AutomationDesigner; 